/**
 * Compliance copy (BCC): one hidden address added to every Outlook email a sender sends.
 * The sender saves it once (users/{uid}.outlookComplianceBccAddress). Each campaign stores
 * the address it used (campaigns/{id}.bccAddress).
 * Shared by createCampaign, the reply handler, and the React send screens. No address is
 * ever hardcoded here.
 */

const { COUNTRY_MAPPINGS } = require('./investorCountryMappings.js');

const COMPLIANCE_BCC_MAX_LENGTH = 254;
const COMPLIANCE_BCC_INVALID_MESSAGE = 'Compliance copy address is not a valid email.';

// One plain address only: no names, lists, quotes, brackets, or line breaks.
const LOCAL_PART = /^[a-z0-9._%+-]{1,64}$/;
const DOMAIN = /^[a-z0-9-]+(\.[a-z0-9-]+)+$/;

/**
 * Cleaned compliance address.
 * Returns the trimmed, lowercased address when valid, '' when empty (no copy),
 * and null when the value is not exactly one valid email address.
 */
const normalizeBccAddress = (raw) => {
  if (raw === undefined || raw === null) return '';
  if (typeof raw !== 'string') return null;
  const value = raw.trim().toLowerCase();
  if (!value) return '';
  if (value.length > COMPLIANCE_BCC_MAX_LENGTH) return null;
  const parts = value.split('@');
  if (parts.length !== 2) return null;
  const [local, domain] = parts;
  if (!LOCAL_PART.test(local) || !DOMAIN.test(domain)) return null;
  return value;
};

/** The one-entry list stored on a communications row, or [] when there was no copy. */
const bccRecipientsForRow = (address) => {
  const clean = normalizeBccAddress(address);
  return clean ? [{ name: '', address: clean }] : [];
};

/**
 * True when a stored country or location string resolves to United States
 * (for example 'United States', 'US', 'USA', 'U.S.', 'U.S.A.', 'United States of America').
 * Blank, 'Global', city names, and non-US countries return false.
 */
const isUsComplianceCountry = (raw) => {
  if (typeof raw !== 'string') return false;
  const trimmed = raw.trim();
  if (!trimmed) return false;
  if (trimmed === 'United States') return true;
  const lower = trimmed.toLowerCase();
  if (lower === 'united states') return true;
  return COUNTRY_MAPPINGS[lower] === 'United States';
};

module.exports = {
  COMPLIANCE_BCC_MAX_LENGTH,
  COMPLIANCE_BCC_INVALID_MESSAGE,
  normalizeBccAddress,
  bccRecipientsForRow,
  isUsComplianceCountry,
};
